import { Navigate, useNavigate } from "react-router";

import type { SelfUpdateRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { toUiLanguageId } from "@/helpers/languages";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSlug } from "@/hooks/useSlug";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import {
  OnboardingUserProfileForm,
  type OnboardingUserProfileFormProps,
  type OnboardingUserProfileFormValues,
} from "./OnboardingUserProfileForm";

export function OnboardingUserProfilePage(): React.ReactNode {
  const sessionUser = useSessionUser();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  useDocumentTitle(t("page.onboarding.user-details.title.2"));
  const navigate = useNavigate();
  const slug = useSlug();

  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const updateSelfMutation = selfAccountMutations.useUpdateSelf();

  if (sessionUser.onboardedAt) {
    return <Navigate to="/" />;
  }

  const formProps: OnboardingUserProfileFormProps = {
    defaultValues: {
      firstName: sessionUser.firstName,
      lastName: sessionUser.lastName,
      biography: undefined,
      company: sessionUser.company?.name,
      occupation: undefined,
    },
    isCompanyProject: sessionUser.project.type === "companyBased",
    async onSubmit(formValues: OnboardingUserProfileFormValues) {
      const payload: SelfUpdateRequest = {
        ...formValues,
        languageId: toUiLanguageId(sessionUser.language.id),
        hideEmail: sessionUser.emailHidden,
        hidePhoneNumber: sessionUser.phoneNumberHidden,
        hideHouseNumber: sessionUser.hideHouseNumber,
        enableChat: sessionUser.chatPermission !== "disabled",
        phoneNumber: sessionUser.phoneNumber,
      };

      if (formValues.avatar && formValues.avatar.length > 0) {
        const avatarImage = await uploadFormImage(formValues.avatar?.[0]);

        payload.avatarId = avatarImage?.id;
      }

      updateSelfMutation.mutate(payload, {
        onError() {
          showFlashToast({ message: t("page.onboarding.user-details.submit.error"), variant: "alert" });
        },
        onSuccess() {
          void navigate(routes.onboardingFlow.autoTranslationSetting({ slug }));
        },
      });
    },
    isSubmitting: updateSelfMutation.isPending || isUploadingImage,
  };

  return <OnboardingUserProfileForm {...formProps} />;
}
